import assetManifest from "../data/assets.json" with { type: "json" };
import galaxy from "../data/galaxy.json" with { type: "json" };
import balance from "../data/balance.json" with { type: "json" };
import type {
  FactionId,
  Role,
  Technology,
  ShipClass,
  GalaxyNode,
} from "./types";
export const GALAXY = galaxy;
export const BALANCE = balance;
export const FACTIONS: FactionId[] = [
  "alliance",
  "greys",
  "reptiloids",
  "okalani",
  "xilur",
  "nocturn",
];
export const ROLES: Role[] = ["vanguard", "guardian", "navigator"];
export const SHIPS: ShipClass[] = ["scout", "cruiser", "dreadnought"];
export const TECHS: Technology[] = [
  "weapons",
  "shields",
  "propulsion",
  "infrastructure",
];
export const NODES: Record<string, GalaxyNode> = Object.fromEntries(
  galaxy.nodes.map((n) => [n.id, n]),
);
export const HOME = Object.fromEntries(
  FACTIONS.map((f) => [f, galaxy.nodes.find((n) => n.startOwner === f)!.id]),
) as Record<FactionId, string>;
export const PREFERENCES: Record<FactionId, Technology[]> = {
  alliance: ["infrastructure", "shields"],
  greys: ["propulsion", "weapons"],
  reptiloids: ["weapons", "propulsion"],
  okalani: ["shields", "infrastructure"],
  xilur: ["infrastructure", "weapons"],
  nocturn: ["shields", "weapons"],
};
export const PREFERRED_ROLE: Record<FactionId, Role> = {
  alliance: "navigator",
  greys: "navigator",
  reptiloids: "vanguard",
  okalani: "guardian",
  xilur: "guardian",
  nocturn: "vanguard",
};
export const COMMANDERS: Record<FactionId, Record<Role, string>> = {
  alliance: {
    vanguard: "Kim Smith",
    guardian: "Ian Chen",
    navigator: "Eric Vale",
  },
  greys: {
    vanguard: "Keth Vey",
    guardian: "Seyra Nul",
    navigator: "Orin Thaal",
  },
  reptiloids: {
    vanguard: "Ssar Khar",
    guardian: "Vessa Ruun",
    navigator: "Korr Tal",
  },
  okalani: {
    vanguard: "Neri Thal",
    guardian: "Saela Moir",
    navigator: "Iru Pel",
  },
  xilur: {
    vanguard: "Valen Arct",
    guardian: "Regent Maelis",
    navigator: "Ser Kael",
  },
  nocturn: {
    vanguard: "Vael Nox",
    guardian: "Esh Veyr",
    navigator: "Nyra Vaal",
  },
};
export function distance(
  a: string,
  b: string,
  max = 36,
  nodes = NODES,
): number {
  if (a === b) return 0;
  const seen = new Set([a]);
  let frontier = [a];
  for (let d = 1; d <= max; d++) {
    const next: string[] = [];
    for (const id of frontier)
      for (const n of nodes[id]?.neighbors ?? []) {
        if (n === b) return d;
        if (!seen.has(n)) {
          seen.add(n);
          next.push(n);
        }
      }
    frontier = next;
    if (!frontier.length) break;
  }
  return Infinity;
}
export function within(a: string, r: number, nodes = NODES) {
  const seen = new Set([a]);
  let frontier = [a];
  for (let step = 0; step < r; step++) {
    const next: string[] = [];
    for (const id of frontier)
      for (const neighbor of nodes[id]?.neighbors ?? []) {
        if (!seen.has(neighbor)) {
          seen.add(neighbor);
          next.push(neighbor);
        }
      }
    frontier = next;
  }
  return [...seen];
}
export function rank(xp: number) {
  return BALANCE.admirals.xpThreshold.filter((t) => xp >= t).length;
}
export function maxMoves(role: Role, xp: number) {
  const r = rank(xp);
  return Math.min(
    4,
    (role === "navigator" ? 2 : 1) + (r >= 3 ? 1 : 0) + (r >= 5 ? 1 : 0),
  );
}
const assetFiles = new Set(assetManifest.files);
export function asset(path: string) {
  if (/^stations\/[^/]+\.webp$/.test(path))
    path = path.replace("stations/", "stations/refined/");
  if (!assetFiles.has(path)) throw new Error("Unknown local asset: " + path);
  return `${import.meta.env?.BASE_URL ?? "./"}assets/${path}${path === "factions/alliance/navigator.webp" ? "?v=1.2.0" : ""}`;
}
